import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { requestCache } from './cache';
import { isBackendRequest } from './utils';

import { classifySessionError, forceRefreshSession } from 'core/services/Auth';
import { checkBackendHealth } from 'core/services/health';
import { logout } from 'core/utils/logout';
import { routePaths } from 'modules/auth/constants';

const MAINTENANCE_REDIRECT_STORAGE_KEY = 'maintenanceRedirectAt';
const MAINTENANCE_REDIRECT_COOLDOWN_MS = 30000;

type AuthRetryRequestConfig = InternalAxiosRequestConfig & {
  authRetryAttempted?: boolean;
};

export const createErrorInterceptor = (httpClient: AxiosInstance) => async (error: AxiosError) => {
  if (error.config) {
    const { baseURL, data, method, url, params } = error.config;

    if (requestCache.has(JSON.stringify({ baseURL, data, method, url, params }))) {
      requestCache.delete(JSON.stringify({ baseURL, data, method, url, params }));
    }
  }

  if (error.message === 'Cancel request') {
    return Promise.reject(error);
  }

  if (error.response === undefined) {
    return Promise.reject(error);
  }

  if (error.request === undefined) {
    return Promise.reject(error);
  }

  const {
    response: { status },
  } = error;

  const retryConfig = error.config as AuthRetryRequestConfig | undefined;

  if (
    status === 401 &&
    retryConfig &&
    isBackendRequest(retryConfig) &&
    !retryConfig.authRetryAttempted
  ) {
    retryConfig.authRetryAttempted = true;

    try {
      const session = await forceRefreshSession();

      retryConfig.headers.set('Authorization', `Bearer ${session.getIdToken().getJwtToken()}`);

      return httpClient.request(retryConfig);
    } catch (refreshError) {
      const sessionError = classifySessionError(refreshError);

      if (sessionError.kind === 'terminal') {
        await logout();
      }

      return Promise.reject(sessionError);
    }
  } else if (status === 503 && window.location.pathname !== routePaths.maintenance) {
    const isHealthy = await checkBackendHealth();

    if (!isHealthy) {
      const now = Date.now();
      let lastRedirect = 0;

      try {
        lastRedirect = Number(sessionStorage.getItem(MAINTENANCE_REDIRECT_STORAGE_KEY) || '0');
      } catch (storageError) {
        console.warn('Unable to read maintenance redirect timestamp.', storageError);
      }

      if (
        !Number.isFinite(lastRedirect) ||
        now - lastRedirect >= MAINTENANCE_REDIRECT_COOLDOWN_MS
      ) {
        try {
          sessionStorage.setItem(MAINTENANCE_REDIRECT_STORAGE_KEY, String(now));
        } catch (storageError) {
          console.warn('Unable to persist maintenance redirect timestamp.', storageError);
        }

        window.location.href = routePaths.maintenance;
      }
    }
  }

  if (status >= 500) {
    console.log('error 500');
  }

  return Promise.reject(error);
};
